/**
 * Button component with Dashboard3 styling
 *
 * Pass `data-testid` for Playwright - same pattern as elsewhere (`page.getByTestId`).
 *
 * @example
 * ```tsx
 * <Button variant="primary">Click me</Button>
 * <Button variant="outline" size="sm">Cancel</Button>
 * <Button data-testid="submit-form">Submit</Button>
 * ```
 */

"use client";

import {
  Button as ChakraButton,
  type ButtonProps as ChakraButtonProps,
} from "@chakra-ui/react";
import { forwardRef, type AnchorHTMLAttributes } from "react";
import { useInteractiveTokens, useThemeTokens } from "@/lib/theme/tokens";
import { useClassicDashboardStyles } from "@/lib/hooks/useClassicDashboardStyles";

export interface ButtonProps
  extends Omit<ChakraButtonProps, "colorScheme">,
    Pick<AnchorHTMLAttributes<HTMLAnchorElement>, "href" | "target" | "rel"> {
  /**
   * Button variant
   * - primary: Accent primary color (CTAs, main actions)
   * - muted: Accent muted color (secondary actions)
   * - outline: Outlined button
   * - ghost: Transparent button
   */
  variant?: "primary" | "muted" | "outline" | "ghost";
  h?: string;
}

/** Center spinner when `isLoading` without `loadingText` (Chakra uses absolute positioning otherwise). */
const loadingSpinnerCenterSx = {
  "&[data-loading] .chakra-button__spinner": {
    position: "absolute",
    inset: "0",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "0 !important",
  },
} as const;

export const Button = forwardRef<HTMLElement, ButtonProps>(
  (
    { variant = "primary", h = "44px", px, sx: sxProp, loadingText, ...props },
    ref,
  ) => {
    const theme = useThemeTokens();
    const interactive = useInteractiveTokens();
    const { controlBorderRadius } = useClassicDashboardStyles();

    const disabledPrimaryHover = {
      bg: `${theme.accent.primary} !important`,
      color: "white !important",
      opacity: "0.5 !important",
      cursor: "not-allowed",
    };

    const primaryActiveBg = {
      bg: `${theme.accent.primary} !important`,
      color: "white !important",
    };

    const variantStyles = {
      primary: {
        bg: theme.accent.primary,
        color: "white",
        _expanded: primaryActiveBg,
        _active: primaryActiveBg,
        _disabled: {
          bg: theme.accent.primary,
          color: "white",
          opacity: 0.5,
          cursor: "not-allowed",
          pointerEvents: "none",
          _hover: disabledPrimaryHover,
        },
        sx: {
          "&[data-active]": primaryActiveBg,
          "&[data-active]:hover": {
            ...primaryActiveBg,
            opacity: 0.9,
          },
          "&[aria-expanded=true]": primaryActiveBg,
          "@media (hover: hover)": {
            "&:hover:not(:disabled):not([disabled]):not([aria-disabled=true]):not([data-loading])":
              {
                bg: interactive.button.primary.hover,
              },
            "&:hover:disabled, &:disabled:hover, &[aria-disabled=true]:hover":
              disabledPrimaryHover,
            "&:hover[data-loading]": {
              bg: `${theme.accent.primary} !important`,
            },
          },
          "@media (hover: none) and (pointer: coarse)": {
            "&:active:not(:disabled):not([disabled]):not([aria-disabled=true]):not([data-loading])":
              {
                bg: interactive.button.primary.active,
              },
            "&:active[data-loading]": {
              bg: `${theme.accent.primary} !important`,
            },
          },
        },
        _focus: {
          boxShadow: theme.focusRing,
        },
        _loading: {
          bg: `${theme.accent.primary} !important`,
          opacity: 0.7,
        },
      },
      muted: {
        bg: theme.accent.mutedBg,
        color: theme.accent.muted,
        _disabled: {
          opacity: 0.5,
          cursor: "not-allowed",
          pointerEvents: "none",
        },
        sx: {
          "@media (hover: hover)": {
            "&:hover:not(:disabled):not([disabled]):not([aria-disabled=true])":
              {
                bg: interactive.button.muted.hover,
              },
          },
          "@media (hover: none) and (pointer: coarse)": {
            "&:active:not(:disabled):not([disabled]):not([aria-disabled=true])":
              {
                bg: interactive.button.muted.active,
              },
          },
        },
        _focus: {
          boxShadow: theme.focusRing,
        },
      },
      outline: {
        borderWidth: "1px",
        borderColor: theme.borderGlass.default,
        bg: "transparent",
        color: theme.text.primary,
        _disabled: {
          opacity: 0.5,
          cursor: "not-allowed",
          pointerEvents: "none",
        },
        sx: {
          textDecoration: "none",
          "@media (hover: hover)": {
            "&:hover:not(:disabled):not([disabled]):not([aria-disabled=true])":
              {
                bg: interactive.button.ghost.hover,
                borderColor: theme.accent.primary,
                textDecoration: "none",
              },
          },
        },
        _focus: {
          boxShadow: theme.focusRing,
        },
      },
      ghost: {
        bg: "transparent",
        color: theme.text.secondary,
        _disabled: {
          opacity: 0.5,
          cursor: "not-allowed",
          pointerEvents: "none",
        },
        sx: {
          "@media (hover: hover)": {
            "&:hover:not(:disabled):not([disabled]):not([aria-disabled=true])":
              {
                bg: interactive.button.ghost.hover,
                color: theme.text.primary,
              },
          },
        },
        _focus: {
          boxShadow: theme.focusRing,
        },
      },
    };

    const { sx: variantSx, ...variantCss } = variantStyles[variant];
    const mergedSx = {
      ...(typeof variantSx === "object" && variantSx !== null ? variantSx : {}),
      ...(loadingText ? {} : loadingSpinnerCenterSx),
      ...(sxProp ?? {}),
    };

    return (
      <ChakraButton
        ref={ref}
        variant="unstyled"
        display="inline-flex"
        alignItems="center"
        justifyContent="center"
        position="relative"
        h={h}
        px={px ?? 4}
        borderRadius={controlBorderRadius}
        fontWeight="400"
        fontSize="sm"
        textDecoration="none"
        transition="all 0.18s ease-out"
        loadingText={loadingText}
        {...variantCss}
        sx={mergedSx}
        {...props}
      />
    );
  },
);

Button.displayName = "Button";
